'use client'

import { useCallback, useEffect, useRef, useState } from 'react'
import './hero-system-trace.css'

/**
 * The hero's right column: the product system drawn as a five-phase
 * instrument, ported from the design mock `public/new-right.html` (Codex,
 * 2026-08-22; untracked on purpose). A human sets the direction, Radar
 * sweeps, Vector points, Hive multiplies, and control returns to a human.
 *
 * Markup, copy, class names and the SVG are the mock's, extracted
 * programmatically rather than retyped; styling lives in
 * `hero-system-trace.css`. The mock's inline script is this component's
 * state: `phase` drives `data-phase` (every transition and keyframe keys off
 * it), the five controls' `aria-pressed`, the readout panels' `aria-hidden`
 * and the footer status line.
 *
 * Playback is the mock's, to the millisecond: the server renders the resting
 * `checkpoint` state; once the figure is 30% in view the sequence starts
 * 280ms later and steps radar → vector → hive → checkpoint at 2.5s intervals,
 * then holds. Replay restarts it; a phase button stops it and jumps. Reduced
 * motion shows the end state and never animates. A hidden tab stops the
 * clock, and an interrupted run resolves to the end state on return.
 *
 * Deviations from the mock: the figcaption uses the Tailwind `sr-only`
 * utility instead of the mock's own rule, and the mock's one em dash in the
 * direction readout became a comma (site copy rule: no em/en dashes).
 */

type Phase = 'direction' | 'radar' | 'vector' | 'hive' | 'checkpoint'

/** The mock's `phaseSchedule`: milliseconds after play starts. */
const SCHEDULE: ReadonlyArray<readonly [Phase, number]> = [
  ['radar', 2500],
  ['vector', 5500],
  ['hive', 8500],
  ['checkpoint', 11500],
]

const STATUS: Record<Phase, string> = {
  direction: 'Human direction received',
  radar: 'Radar · measuring visibility',
  vector: 'Vector · qualifying the move',
  hive: 'Hive · building the system',
  checkpoint: 'Human checkpoint ready',
}

const REDUCED_MOTION = '(prefers-reduced-motion: reduce)'

export default function HeroSystemTrace() {
  const [phase, setPhase] = useState<Phase>('checkpoint')
  const figureRef = useRef<HTMLElement>(null)
  const timers = useRef<number[]>([])
  const hasPlayed = useRef(false)
  /** True while a played sequence (auto-play or Replay) is still stepping. */
  const sequenceActive = useRef(false)
  const observer = useRef<IntersectionObserver | null>(null)

  const clearSequence = useCallback(() => {
    timers.current.forEach(window.clearTimeout)
    timers.current = []
  }, [])

  /** Stop the clock and stop watching the viewport: the user is in control. */
  const takeControl = useCallback(() => {
    clearSequence()
    sequenceActive.current = false
    hasPlayed.current = true
    observer.current?.disconnect()
  }, [clearSequence])

  const playSequence = useCallback(() => {
    takeControl()
    if (window.matchMedia(REDUCED_MOTION).matches) {
      setPhase('checkpoint')
      return
    }
    sequenceActive.current = true
    setPhase('direction')
    for (const [next, at] of SCHEDULE) {
      timers.current.push(
        window.setTimeout(() => {
          setPhase(next)
          if (next === 'checkpoint') sequenceActive.current = false
        }, at)
      )
    }
  }, [takeControl])

  const jumpTo = useCallback(
    (next: Phase) => {
      takeControl()
      setPhase(next)
    },
    [takeControl]
  )

  useEffect(() => {
    const reduceMotion = window.matchMedia(REDUCED_MOTION)
    const onMotionChange = () => {
      clearSequence()
      if (reduceMotion.matches) {
        hasPlayed.current = true
        sequenceActive.current = false
        observer.current?.disconnect()
        setPhase('checkpoint')
      }
    }
    reduceMotion.addEventListener('change', onMotionChange)

    // A hidden tab stops the clock. On return, only a sequence that was
    // interrupted mid-run resolves to its end state; a phase the user chose
    // is left alone.
    const onVisibilityChange = () => {
      if (document.hidden) {
        clearSequence()
      } else if (sequenceActive.current) {
        sequenceActive.current = false
        setPhase('checkpoint')
      }
    }
    document.addEventListener('visibilitychange', onVisibilityChange)

    // Resting state is already rendered; only arm playback when motion is
    // allowed. Without IntersectionObserver the mock simply plays on load.
    if (!reduceMotion.matches) {
      if (typeof IntersectionObserver === 'undefined') {
        timers.current.push(window.setTimeout(playSequence, 420))
      } else if (figureRef.current) {
        observer.current = new IntersectionObserver(
          entries => {
            const visible = entries.some(
              entry => entry.isIntersecting && entry.intersectionRatio >= 0.3
            )
            if (visible && !hasPlayed.current) {
              timers.current.push(window.setTimeout(playSequence, 280))
              observer.current?.disconnect()
            }
          },
          { threshold: [0.3] }
        )
        observer.current.observe(figureRef.current)
      }
    }

    return () => {
      clearSequence()
      observer.current?.disconnect()
      reduceMotion.removeEventListener('change', onMotionChange)
      document.removeEventListener('visibilitychange', onVisibilityChange)
    }
  }, [clearSequence, playSequence])

  return (
    <figure
      ref={figureRef}
      id='system-trace'
      className='hst-instrument'
      data-phase={phase}
      aria-labelledby='instrument-title'
      aria-describedby='instrument-description'
    >
      <figcaption id='instrument-description' className='sr-only'>
        An animated demonstration of the Pixelmojo product system. A human sets
        the direction, Radar measures visibility, Vector qualifies the move,
        Hive builds the system, and control returns to a human for review.
      </figcaption>

      <div className='hst-instrument__head'>
        <div>
          <span className='hst-instrument__kicker'>The directed grid</span>
          <strong className='hst-instrument__title' id='instrument-title'>
            One grid. Three verbs.
          </strong>
        </div>

        <button
          className='hst-replay'
          type='button'
          onClick={playSequence}
          aria-label='Replay the product sequence'
        >
          <svg viewBox='0 0 16 16' aria-hidden='true'>
            <path d='M13.2 6.1A5.5 5.5 0 1 0 13 10.4M13.2 2.8v3.3H9.9' />
          </svg>
          <span>Replay</span>
        </button>
      </div>

      <div className='hst-instruction'>
        <span className='hst-instruction__index' aria-hidden='true'>
          H
        </span>
        <div>
          <span className='hst-instruction__label'>
            Human direction · buyer question
          </span>
          <q>Which brand should I choose?</q>
        </div>
        <span className='hst-instruction__meta'>Set the constraint</span>
      </div>

      <div className='hst-instrument__stage'>
        <div className='hst-mark-field' aria-hidden='true'>
          <span className='hst-mark-field__coordinate'>64 × 64 · 4px base</span>

          <svg className='hst-product-mark' viewBox='-10 -8 84 80' fill='none'>
            <rect
              className='hst-mark-guide'
              x='0'
              y='0'
              width='64'
              height='64'
            />
            <line className='hst-mark-axis' x1='32' y1='-5' x2='32' y2='69' />
            <line className='hst-mark-axis' x1='-5' y1='32' x2='69' y2='32' />

            <g className='hst-mark-base'>
              <rect
                className='hst-mark-cell'
                x='4'
                y='4'
                width='26'
                height='26'
              />
              <rect
                className='hst-mark-cell'
                x='34'
                y='34'
                width='26'
                height='26'
              />
            </g>

            <g className='hst-mark-layer hst-mark-layer--direction'>
              <rect
                className='hst-mark-cell'
                x='4'
                y='4'
                width='26'
                height='26'
              />
              <rect
                className='hst-mark-cell--muted'
                x='34'
                y='4'
                width='26'
                height='26'
              />
              <rect
                className='hst-mark-cell--muted'
                x='4'
                y='34'
                width='26'
                height='26'
              />
              <rect
                className='hst-mark-cell'
                x='34'
                y='34'
                width='26'
                height='26'
              />
              <path className='hst-mark-route' d='M-8 47H10' />
              <rect
                className='hst-mark-route-packet hst-direction-packet'
                x='10'
                y='41'
                width='12'
                height='12'
              />
            </g>

            <g className='hst-mark-layer hst-mark-layer--radar'>
              <rect
                className='hst-mark-cell'
                x='4'
                y='4'
                width='26'
                height='26'
              />
              <path
                className='hst-mark-verb hst-radar-verb'
                d='M34 4 L34 30 A26 26 0 0 0 60 4 Z'
              />
              <circle
                className='hst-mark-answer hst-radar-blip'
                cx='17'
                cy='47'
                r='8'
              />
              <rect
                className='hst-mark-cell'
                x='34'
                y='34'
                width='26'
                height='26'
              />
            </g>

            <g className='hst-mark-layer hst-mark-layer--vector'>
              <rect
                className='hst-mark-cell'
                x='4'
                y='4'
                width='26'
                height='26'
              />
              <path
                className='hst-mark-verb hst-vector-verb'
                d='M34 4 L60 4 L60 30 Z'
              />
              <path
                className='hst-mark-answer hst-vector-origin'
                d='M9 39 L25 39 L25 55 Z'
              />
              <rect
                className='hst-mark-cell'
                x='34'
                y='34'
                width='26'
                height='26'
              />
            </g>

            <g className='hst-mark-layer hst-mark-layer--hive'>
              <rect
                className='hst-mark-cell'
                x='4'
                y='4'
                width='26'
                height='26'
              />
              <rect
                className='hst-mark-verb hst-hive-unit hst-hive-unit--1'
                x='34'
                y='4'
                width='11'
                height='11'
              />
              <rect
                className='hst-mark-verb hst-hive-unit hst-hive-unit--2'
                x='49'
                y='4'
                width='11'
                height='11'
              />
              <rect
                className='hst-mark-verb hst-hive-unit hst-hive-unit--3'
                x='34'
                y='19'
                width='11'
                height='11'
              />
              <rect
                className='hst-mark-verb hst-hive-unit hst-hive-unit--4'
                x='49'
                y='19'
                width='11'
                height='11'
              />
              <rect
                className='hst-mark-answer'
                x='11.5'
                y='41.5'
                width='11'
                height='11'
              />
              <rect
                className='hst-mark-cell'
                x='34'
                y='34'
                width='26'
                height='26'
              />
            </g>

            <g className='hst-mark-layer hst-mark-layer--checkpoint'>
              <rect
                className='hst-mark-checkpoint'
                x='6.5'
                y='36.5'
                width='21'
                height='21'
              />
            </g>

            <path className='hst-signal-path' d='M-6 47 H17 L32 32 L47 17' />
            <rect
              className='hst-signal-marker'
              x='-8'
              y='45'
              width='4'
              height='4'
            />
          </svg>

          <div className='hst-phase-tag'>
            <span className='hst-phase-tag__direction'>00 · Direct</span>
            <span className='hst-phase-tag__radar'>01 · Sweep</span>
            <span className='hst-phase-tag__vector'>02 · Point</span>
            <span className='hst-phase-tag__hive'>03 · Swarm</span>
            <span className='hst-phase-tag__checkpoint'>04 · Review</span>
          </div>
        </div>

        <div className='hst-readout'>
          <article
            className='hst-readout-panel hst-readout-panel--direction'
            aria-hidden={phase !== 'direction'}
          >
            <span className='hst-readout__eyebrow'>Human direction</span>
            <div className='hst-readout__title'>Set the constraint.</div>
            <p>
              The system starts with an explicit question, not autonomous
              motion.
            </p>
            <div className='hst-readout__metric'>
              <strong>01</strong>
              <span>Clear instruction</span>
            </div>
          </article>

          <article
            className='hst-readout-panel hst-readout-panel--radar'
            aria-hidden={phase !== 'radar'}
          >
            <span className='hst-readout__eyebrow'>01 · Measure</span>
            <div className='hst-readout__title'>Radar sweeps.</div>
            <p>
              Audit how AI reads your brand and isolate the issue with the fix
              attached.
            </p>
            <div className='hst-readout__metric'>
              <strong>13</strong>
              <span>Visibility checks</span>
            </div>
          </article>

          <article
            className='hst-readout-panel hst-readout-panel--vector'
            aria-hidden={phase !== 'vector'}
          >
            <span className='hst-readout__eyebrow'>02 · Qualify</span>
            <div className='hst-readout__title'>Vector points.</div>
            <p>
              Turn evidence into a deliberate route, then automate the
              follow-through.
            </p>
            <div className='hst-readout__metric'>
              <strong>12</strong>
              <span>Scoring dimensions</span>
            </div>
          </article>

          <article
            className='hst-readout-panel hst-readout-panel--hive'
            aria-hidden={phase !== 'hive'}
          >
            <span className='hst-readout__eyebrow'>03 · Build</span>
            <div className='hst-readout__title'>Hive multiplies.</div>
            <p>
              Coordinate AI workers around the chosen route with human control
              built in.
            </p>
            <div className='hst-readout__metric'>
              <strong>×4</strong>
              <span>One cell becomes many</span>
            </div>
          </article>

          <article
            className='hst-readout-panel hst-readout-panel--checkpoint'
            aria-hidden={phase !== 'checkpoint'}
          >
            <span className='hst-readout__eyebrow'>Return to human</span>
            <div className='hst-readout__title'>Review. Approve. Redirect.</div>
            <p>
              The motion resolves where ownership belongs: with your team and
              your next decision.
            </p>
            <div className='hst-readout__metric'>
              <strong>✓</strong>
              <span>System you own</span>
            </div>
          </article>
        </div>
      </div>

      <div
        className='hst-phase-controls'
        role='group'
        aria-label='Choose an animation stage'
      >
        <button
          className='hst-phase-control'
          type='button'
          aria-pressed={phase === 'direction'}
          onClick={() => jumpTo('direction')}
        >
          Human
        </button>
        <button
          className='hst-phase-control'
          type='button'
          aria-pressed={phase === 'radar'}
          onClick={() => jumpTo('radar')}
        >
          Radar
        </button>
        <button
          className='hst-phase-control'
          type='button'
          aria-pressed={phase === 'vector'}
          onClick={() => jumpTo('vector')}
        >
          Vector
        </button>
        <button
          className='hst-phase-control'
          type='button'
          aria-pressed={phase === 'hive'}
          onClick={() => jumpTo('hive')}
        >
          Hive
        </button>
        <button
          className='hst-phase-control'
          type='button'
          aria-pressed={phase === 'checkpoint'}
          onClick={() => jumpTo('checkpoint')}
        >
          Review
        </button>
      </div>

      <div className='hst-instrument__footer'>
        <span className='hst-instrument__status'>{STATUS[phase]}</span>
        <span>Finite motion · holds on result</span>
      </div>
    </figure>
  )
}
